import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { Person } from '@store/reducers/beneficiaries/beneficiaries.reducer';
import { ButtonComponent } from '@vg-constellation/angular-16/button';
import { HintErrorModule } from '@vg-constellation/angular-16/hint-error';
import { InputMask, InputModule } from '@vg-constellation/angular-16/input';
import { LabelModule } from '@vg-constellation/angular-16/label';
import { LinkModule } from '@vg-constellation/angular-16/link';
import { RadioModule } from '@vg-constellation/angular-16/radio';
import { IMaskModule } from 'angular-imask';
import { combineLatest, Subject, takeUntil } from 'rxjs';

import { BeneCardComponent } from '../beneficiary-card/beneficiary-card.component';

const DATE_NUMBER_OF_DIGITS = 8;
export const EMPTY_MASKED_DATE = '__/__/____';

export interface BeneficiaryRow {
  label: string;
  value: string;
}

export interface BeneficiaryChildrenCardRow {
  rows: BeneficiaryRow[];
}

export interface BeneficiaryCardRow {
  rows: BeneficiaryRow[];
  header: string;
  children?: BeneficiaryChildrenCardRow[];
}

@Component({
  selector: 'app-decedent-base-info',
  templateUrl: './decedent-base-info.component.html',
  styleUrls: ['./decedent-base-info.component.scss'],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ButtonComponent,
    LabelModule,
    LinkModule,
    InputModule,
    IMaskModule,
    AsyncPipe,
    HintErrorModule,
    RadioModule,
    BeneCardComponent,
  ],
})
export class DecedentBaseInfoComponent implements OnInit, OnDestroy {
  @Input() public content: any;
  @Input() public beneficiaries: Person[] = [];
  @Output() submit = new EventEmitter<BeneficiaryCardRow[]>();
  @Output() edit = new EventEmitter<BeneficiaryCardRow[]>();
  @Output() add = new EventEmitter<void>();
  @Output() remove = new EventEmitter<BeneficiaryCardRow[]>();
  @Output() cancel = new EventEmitter<void>();

  private fb: UntypedFormBuilder = inject(UntypedFormBuilder);
  private destroy$ = new Subject<void>();
  public inputMask = InputMask;
  public decedentFormGroup: UntypedFormGroup = this.fb.group({
    fullLegalName: new UntypedFormControl('', Validators.required),
    dateOfBirth: new UntypedFormControl('', [this.validateDateRequired]),
    dateOfDeath: new UntypedFormControl('', [this.dateOfDeathValidator.bind(this)]),
    hasChildren: new UntypedFormControl(''),
    children: this.fb.array([]),
  });
  public firstSave: boolean = false;
  public isSavedState: boolean = false;
  public addMore: boolean = false;
  public beneArray: BeneficiaryCardRow[] = [];
  public isEditMode: boolean = false;
  public editIndex: number | null = null;
  public isChildrenQuestionShown: boolean = false;

  ngOnInit(): void {
    if (this.beneficiaries?.length) {
      this.prefillBeneArray(this.beneficiaries);
      this.isEditMode = false;
      this.isSavedState = true;
    }

    this.decedentFormGroup
      .get('hasChildren')!
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (hasChildren: string) => {
          if (hasChildren === 'Yes') {
            if (!this.decedentFormGroup.get('children')!.value?.length) {
              this.addChild();
            }
          } else {
            this.decedentFormGroup.setControl('children', this.fb.array([]), { emitEvent: false });
          }
        },
      });

    combineLatest([
      this.decedentFormGroup.get('dateOfDeath')!.valueChanges,
      this.decedentFormGroup.get('dateOfBirth')!.valueChanges,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setChildrenControl();
      });

    this.edit
      .asObservable()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.setChildrenControl();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showRemoveButton(): boolean {
    return this.beneArray?.length > 1;
  }

  get showCancelButton(): boolean {
    return this.beneArray?.length > 0;
  }

  get newCardTitle(): string {
    return this.getCardTitle(this.beneArray.length);
  }

  get cardTitle(): string {
    return this.isEditMode ? this.getCardTitle(this.editIndex) : this.newCardTitle;
  }

  getCardTitle(index: number): string {
    return `${this.content.cardTitle} ${index + 1}`;
  }

  get children(): UntypedFormArray {
    return this.decedentFormGroup.get('children') as UntypedFormArray;
  }

  get hasChildrenSelected(): boolean {
    return this.decedentFormGroup.get('hasChildren')!.value === 'Yes';
  }

  prefillBeneArray(beneficiaries: Person[]): void {
    this.beneArray = beneficiaries.map(
      (bene: Person, index: number): BeneficiaryCardRow => ({
        header: this.getCardTitle(index),
        rows: [
          {
            label: this.content.fullLegalName.label,
            value: bene.name,
          },
          {
            label: this.content.dateOfBirth.label,
            value: bene.dateGroup.dob,
          },
          {
            label: this.content.dateOfDeath.label,
            value: bene.dateGroup.dod,
          },
        ],
        children: bene.children?.map(
          (child: Person): BeneficiaryChildrenCardRow => ({
            rows: [
              {
                label: this.content.fullLegalName.label,
                value: child.name,
              },
              {
                label: this.content.dateOfBirth.label,
                value: child.dateGroup.dob,
              },
              {
                label: this.content.dateOfDeath.label,
                value: child.dateGroup.dod,
              },
            ],
          }),
        ),
      }),
    );
  }

  handleSubmit(): void {
    this.firstSave = true;
    this.decedentFormGroup.get('dateOfDeath')!.updateValueAndValidity();

    if (this.decedentFormGroup.invalid) {
      return;
    }

    const updatedCard: BeneficiaryCardRow = {
      header: this.isEditMode ? this.beneArray[this.editIndex!].header : this.newCardTitle,
      rows: [
        {
          label: this.content.fullLegalName.label,
          value: this.decedentFormGroup.get('fullLegalName')!.value,
        },
        {
          label: this.content.dateOfBirth.label,
          value: this.decedentFormGroup.get('dateOfBirth')!.value,
        },
        {
          label: this.content.dateOfDeath.label,
          value: this.decedentFormGroup.get('dateOfDeath')!.value || 'N/A',
        },
      ],
      children: this.children.value.map((child: any) => {
        return {
          rows: [
            {
              label: this.content.fullLegalName.label,
              value: child.fullLegalName,
            },
            {
              label: this.content.dateOfBirth.label,
              value: child.dateOfBirth,
            },
            {
              label: this.content.dateOfDeath.label,
              value: child.dateOfDeath || 'N/A',
            },
          ],
        };
      }),
    };

    if (this.isEditMode && this.editIndex !== null) {
      this.beneArray[this.editIndex] = updatedCard;
      this.isEditMode = false;
      this.editIndex = null;
    } else {
      this.beneArray = [...this.beneArray, updatedCard];
    }

    this.isSavedState = true;
    this.addMore = false;
    this.decedentFormGroup.reset();
    this.submit.emit(this.beneArray);
  }

  handleCancel(): void {
    this.isEditMode = false;
    this.editIndex = null;
    this.isSavedState = true;
    this.addMore = false;
    this.decedentFormGroup.reset();
    this.cancel.emit();
  }

  handleRemoveCard(card: BeneficiaryCardRow): void {
    this.beneArray = this.beneArray.filter(({ header }) => header !== card.header);
    this.beneArray.forEach((card, index) => {
      card.header = this.getCardTitle(index);
    });
    this.remove.emit(this.beneArray);
  }

  handleAdd(): void {
    this.firstSave = false;
    this.isSavedState = false;
    this.addMore = true;
    this.decedentFormGroup.reset();
    this.add.emit();
  }

  handleEditCard(card: BeneficiaryCardRow, index: number): void {
    this.isEditMode = true;
    this.editIndex = index;

    this.decedentFormGroup.patchValue(
      {
        fullLegalName:
          card.rows.find((row) => row.label === this.content.fullLegalName.label)?.value || '',
        dateOfBirth:
          card.rows.find((row) => row.label === this.content.dateOfBirth.label)?.value || '',
        dateOfDeath:
          card.rows.find((row) => row.label === this.content.dateOfDeath.label)?.value || '',
        hasChildren: card.children && card.children.length > 0 ? 'Yes' : 'No',
      },
      { emitEvent: false },
    );

    const childrenArray = this.fb.array([]);
    card.children?.forEach((child: BeneficiaryChildrenCardRow) => {
      const childFormGroup = this.fb.group({
        fullLegalName: new UntypedFormControl(
          child.rows.find((row) => row.label === this.content.fullLegalName.label)?.value || '',
          Validators.required,
        ),
        dateOfBirth: new UntypedFormControl(
          child.rows.find((row) => row.label === this.content.dateOfBirth.label)?.value || '',
          [this.validateDateRequired],
        ),
        dateOfDeath: new UntypedFormControl(
          child.rows.find((row) => row.label === this.content.dateOfDeath.label)?.value || '',
          [this.dateOfDeathValidator.bind(this)],
        ),
      });
      childrenArray.push(childFormGroup);
    });

    this.decedentFormGroup.setControl('children', childrenArray);

    this.edit.emit();
  }

  addChild(): void {
    const childrenArray = this.decedentFormGroup.get('children') as UntypedFormArray;
    const formGroup = this.fb.group({
      fullLegalName: new UntypedFormControl('', Validators.required),
      dateOfBirth: new UntypedFormControl('', [this.validateDateRequired]),
      dateOfDeath: new UntypedFormControl('', [this.dateOfDeathValidator.bind(this)]),
    });
    childrenArray.push(formGroup);
  }

  removeChild(index: number) {
    this.children.removeAt(index);
  }

  private dateOfDeathValidator(control: AbstractControl): { [key: string]: boolean } | null {
    const parentGroup = control.parent;

    if (!parentGroup) return null;

    const dateOfBirth = parentGroup.get('dateOfBirth')?.value;
    const dateOfDeath = control.value;

    if (dateOfDeath === EMPTY_MASKED_DATE) {
      control.setValue('');
      return null;
    }

    if (!dateOfBirth || !dateOfDeath || dateOfDeath === 'N/A') {
      return null;
    }

    if (this.validateDateRequired(control)) {
      return { dateOfDeathInvalid: true };
    }

    const dob = new Date(dateOfBirth);
    const dod = new Date(dateOfDeath);

    return dod < dob ? { dateOfDeathInvalid: true } : null;
  }

  private validateDateRequired(control: AbstractControl): { [key: string]: boolean } | null {
    const dateStripped = control.value?.replace(/_/g, '').replace(/\//g, '') || '';
    return dateStripped.length < DATE_NUMBER_OF_DIGITS ? { required: true } : null;
  }

  private setChildrenControl(): void {
    const isDateOfDeathValid =
      !!this.decedentFormGroup.get('dateOfDeath')!.value &&
      !!this.decedentFormGroup.get('dateOfBirth')!.value &&
      this.decedentFormGroup.get('dateOfDeath')!.value !== 'N/A' &&
      !this.dateOfDeathValidator(this.decedentFormGroup.get('dateOfDeath')!);
    this.isChildrenQuestionShown = isDateOfDeathValid;
    if (isDateOfDeathValid) {
      this.decedentFormGroup.get('hasChildren')!.setValidators(Validators.required);
      this.decedentFormGroup.get('hasChildren')!.updateValueAndValidity();
    } else {
      this.decedentFormGroup.get('hasChildren')!.clearValidators();
      this.decedentFormGroup.get('hasChildren')!.updateValueAndValidity();
    }
  }
}
